import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import Server from '../types/Server'
import EccChip from './EccChip'
import { grossPrice, formatEuro, formatDuration, PROVIDER_NAMES } from '../utils/servers'

type ServerDetailsDialogProps = {
  server: Server | null
  onClose: () => void
}

const ServerDetailsDialog: React.FC<ServerDetailsDialogProps> = ({ server, onClose }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog open={server !== null} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      {server && (
        <>
          <DialogTitle sx={{ pr: 6 }}>
            {server.cpu}
            <Typography variant="caption" display="block" color="text.secondary">
              {PROVIDER_NAMES[server.provider]} #{server.id} · {server.datacenter}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
              {formatEuro(grossPrice(server))}
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}
                / month
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatEuro(server.price)} net
              {server.ip_price.Monthly > 0
                ? ` + ${formatEuro(server.ip_price.Monthly)} IPv4`
                : ' incl. IPv4'}
              {' + 19% VAT'}
              {server.setup_price > 0 && ` · ${formatEuro(server.setup_price)} setup`}
            </Typography>
            {!server.fixed_price && server.next_reduce > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }} icon={false}>
                Price drops again in ~{formatDuration(server.next_reduce)}
              </Alert>
            )}

            <List dense sx={{ mt: 1 }}>
              {server.information.map((line, index) => (
                <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
                  <ListItemText primary={line} />
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {server.is_ecc && <EccChip />}
              {server.bandwidth > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    server.bandwidth >= 1000
                      ? `${server.bandwidth / 1000} Gbit/s`
                      : `${server.bandwidth} Mbit/s`
                  }
                />
              )}
              {server.traffic && (
                <Chip size="small" variant="outlined" label={`${server.traffic} traffic`} />
              )}
              {server.specials.map((special) => (
                <Chip key={special} size="small" variant="outlined" label={special} />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
            <Button
              variant="contained"
              endIcon={<OpenInNewIcon />}
              href={server.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View at {PROVIDER_NAMES[server.provider]}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default ServerDetailsDialog
